import { Check, ChevronDown, ChevronRight, X } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { formatEngineLabel } from "@/lib/format";
import type { CryodCatalogCell, CryodMatrixTestStatus } from "@/lib/types";
import { cn } from "@/lib/utils";

// Hardware tiers shown as tiles inside each model x config cell. With no tag
// selected, color encodes the tier so the grid scans by hardware at a glance;
// with a tag selected, color encodes run status (pass/fail/no-run) instead and
// the chip text keeps the tier identity. ``any`` = a tier-agnostic test
// (gpu_type unset).
const TIER_ORDER = ["h100", "h200", "b200", "b300", "any"] as const;

const TIER_STYLES: Record<string, string> = {
  h100: "bg-blue-500/15 text-blue-700 ring-blue-500/30 hover:bg-blue-500/30 dark:text-blue-300",
  h200: "bg-violet-500/15 text-violet-700 ring-violet-500/30 hover:bg-violet-500/30 dark:text-violet-300",
  b200: "bg-emerald-500/15 text-emerald-700 ring-emerald-500/30 hover:bg-emerald-500/30 dark:text-emerald-300",
  b300: "bg-amber-500/15 text-amber-700 ring-amber-500/30 hover:bg-amber-500/30 dark:text-amber-300",
  any: "bg-slate-500/15 text-slate-600 ring-slate-500/30 hover:bg-slate-500/30 dark:text-slate-300",
};

type ChipStatus = "pass" | "fail" | "no-run";

// Pass/fail also carry a ✓/✕ glyph (see STATUS_ICONS) so the two never rely on
// color alone — the chips are small and red/green is the hardest pair to tell
// apart at a glance (and for red-green color vision deficiency). Fills/rings are
// a touch stronger than the tier palette so status reads first; no-run is
// fill-less so it reads as absence.
const STATUS_STYLES: Record<ChipStatus, string> = {
  pass: "bg-emerald-500/15 text-emerald-700 ring-emerald-500/40 hover:bg-emerald-500/30 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-400/50",
  fail: "bg-rose-500/20 text-rose-700 ring-rose-500/50 hover:bg-rose-500/35 dark:bg-rose-500/20 dark:text-rose-300 dark:ring-rose-400/60",
  "no-run": "bg-transparent text-muted-foreground/50 ring-border hover:bg-muted/40",
};

const STATUS_ICONS: Record<ChipStatus, typeof Check | null> = {
  pass: Check,
  fail: X,
  "no-run": null,
};

const STATUS_LEGEND_LABELS: Record<ChipStatus, string> = {
  pass: "pass",
  fail: "fail",
  "no-run": "no runs",
};

// The single place that defines the chip status semantics: each test's latest
// result under the selected tag wins (a passing retry clears an earlier fail);
// skips render as no-run. Across multiple tests on one chip: fail > pass > no-run.
function chipStatusOf(
  cellsAtTier: CryodCatalogCell[],
  statusByTestName: Map<string, CryodMatrixTestStatus>,
): ChipStatus {
  let aggregated: ChipStatus = "no-run";
  for (const cell of cellsAtTier) {
    const latestStatus = statusByTestName.get(cell.name)?.latest_status;
    if (latestStatus === "fail") return "fail";
    if (latestStatus === "pass") aggregated = "pass";
  }
  return aggregated;
}

function statusTooltipLine(
  cell: CryodCatalogCell,
  entry: CryodMatrixTestStatus | undefined,
): string {
  if (!entry) return `${cell.name} — no runs`;
  const counts = `${entry.pass_count}P/${entry.fail_count}F/${entry.skip_count}S`;
  return `${cell.name} — ${entry.latest_status} (${counts})`;
}

function tierOf(cell: CryodCatalogCell): string {
  return cell.gpu_type ?? "any";
}

// Column order: tp-only, then tp+ep, then dp+ep, then named recipes
// (dynamo / trtllm / official) last; numeric within each group.
function configSortKey(config: string): string {
  const tensorParallel = Number(config.match(/tp(\d+)/)?.[1] ?? 0);
  const dataParallel = Number(config.match(/dp(\d+)/)?.[1] ?? 0);
  const named = tensorParallel === 0 && dataParallel === 0;
  const group = named ? 3 : dataParallel ? 2 : config.includes("ep") ? 1 : 0;
  return `${group}-${String(tensorParallel).padStart(3, "0")}-${String(dataParallel).padStart(3, "0")}-${config}`;
}

function tierIndex(tier: string): number {
  const index = TIER_ORDER.indexOf(tier as (typeof TIER_ORDER)[number]);
  return index === -1 ? TIER_ORDER.length : index;
}

// One tile per hardware tier in a cell — deduped across tests at that
// (model, config, tier). A `×N` badge flags multiple tests there (e.g. plain +
// inference-bench); click opens the first, the tooltip lists them all.
function TierChip({
  tier,
  cellsAtTier,
  selectedName,
  onSelect,
  statusByTestName,
}: {
  tier: string;
  cellsAtTier: CryodCatalogCell[];
  selectedName: string | null;
  onSelect: (cell: CryodCatalogCell) => void;
  statusByTestName: Map<string, CryodMatrixTestStatus> | null;
}) {
  const gated = cellsAtTier.find((cell) => cell.min_vllm_version != null);
  const selected = cellsAtTier.some((cell) => cell.name === selectedName);
  const count = cellsAtTier.length;
  const names = statusByTestName
    ? cellsAtTier.map((cell) => statusTooltipLine(cell, statusByTestName.get(cell.name))).join("\n")
    : cellsAtTier.map((cell) => cell.name).join("\n");
  const status = statusByTestName ? chipStatusOf(cellsAtTier, statusByTestName) : null;
  const colorStyle = status ? STATUS_STYLES[status] : (TIER_STYLES[tier] ?? TIER_STYLES.any);
  const StatusIcon = status ? STATUS_ICONS[status] : null;
  return (
    <button
      type="button"
      onClick={() => {
        const first = cellsAtTier[0];
        if (first) onSelect(first);
      }}
      title={
        gated
          ? `${names}\n· needs ${formatEngineLabel(gated.engine)} ≥ ${gated.min_vllm_version}`
          : names
      }
      className={cn(
        "inline-flex items-center rounded-md px-1.5 py-0.5 font-mono text-[10px] ring-1 transition-colors",
        colorStyle,
        selected && "ring-2 ring-offset-1 ring-offset-background",
      )}
    >
      {StatusIcon && <StatusIcon className="mr-0.5 size-2.5 shrink-0" strokeWidth={3} />}
      {tier}
      {gated && <span className="ml-0.5 opacity-70">*</span>}
      {count > 1 && <span className="ml-0.5 opacity-60">×{count}</span>}
    </button>
  );
}

export function CoverageLegend({ statusMode = false }: { statusMode?: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
      {statusMode ? (
        <>
          <span className="font-medium uppercase tracking-wider">Status</span>
          {(Object.keys(STATUS_STYLES) as ChipStatus[]).map((status) => {
            const StatusIcon = STATUS_ICONS[status];
            return (
              <span key={status} className="inline-flex items-center gap-1.5">
                <span
                  className={cn(
                    "inline-flex size-3.5 items-center justify-center rounded-sm ring-1",
                    STATUS_STYLES[status],
                  )}
                >
                  {StatusIcon && <StatusIcon className="size-2.5" strokeWidth={3} />}
                </span>
                {STATUS_LEGEND_LABELS[status]}
              </span>
            );
          })}
        </>
      ) : (
        <>
          <span className="font-medium uppercase tracking-wider">Hardware</span>
          {TIER_ORDER.map((tier) => (
            <span key={tier} className="inline-flex items-center gap-1.5">
              <span className={cn("inline-block size-2.5 rounded-sm ring-1", TIER_STYLES[tier])} />
              {tier}
            </span>
          ))}
        </>
      )}
      <span className="text-muted-foreground/70">
        <span className="font-mono">*</span> = min engine version gate
      </span>
    </div>
  );
}

export function CoverageMatrix({
  cells,
  selectedName,
  onSelect,
  statusByTestName = null,
}: {
  cells: CryodCatalogCell[];
  selectedName: string | null;
  onSelect: (cell: CryodCatalogCell) => void;
  statusByTestName?: Map<string, CryodMatrixTestStatus> | null;
}) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const { configs, familiesSorted, familyToModels, byKey } = useMemo(() => {
    const configSet = new Set<string>();
    const byKey = new Map<string, CryodCatalogCell[]>();
    const familyModels = new Map<string, Set<string>>();
    for (const cell of cells) {
      configSet.add(cell.config);
      const key = `${cell.model_id} ${cell.config}`;
      const list = byKey.get(key) ?? [];
      list.push(cell);
      byKey.set(key, list);
      const models = familyModels.get(cell.family) ?? new Set<string>();
      models.add(cell.model_id);
      familyModels.set(cell.family, models);
    }
    const configs = [...configSet].sort((left, right) =>
      configSortKey(left).localeCompare(configSortKey(right)),
    );
    const familyToModels = new Map<string, string[]>();
    for (const [family, models] of familyModels) {
      familyToModels.set(
        family,
        [...models].sort((left, right) => left.localeCompare(right)),
      );
    }
    const familiesSorted = [...familyToModels.keys()].sort((left, right) =>
      left.localeCompare(right),
    );
    return { configs, familiesSorted, familyToModels, byKey };
  }, [cells]);

  const toggleFamily = (family: string) => {
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(family)) next.delete(family);
      else next.add(family);
      return next;
    });
  };

  if (cells.length === 0) {
    return (
      <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
        No declared tests match the current filters.
      </div>
    );
  }

  // border-separate (not collapse) so per-cell borders survive on sticky cells;
  // outer edges come from the container, cells draw right+bottom only.
  const headCell =
    "sticky top-0 z-20 border-b border-r border-border/60 bg-background px-2 py-1.5 text-center text-[11px] font-medium uppercase tracking-wider text-muted-foreground";
  const modelCell =
    "sticky left-0 z-10 border-b border-r border-border/60 bg-background px-3 py-1.5 font-mono text-xs text-foreground whitespace-nowrap";
  const dataCell =
    "border-b border-r border-border/60 px-1.5 py-1 text-center align-middle min-w-[76px]";

  return (
    <div className="max-h-[calc(100vh-14rem)] overflow-auto rounded-lg border border-border">
      <table className="border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            <th className={cn(headCell, "left-0 z-30 min-w-[15rem] text-left")}>Model</th>
            {configs.map((config) => (
              <th key={config} className={cn(headCell, "min-w-[76px] font-mono normal-case")}>
                {config}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {familiesSorted.map((family) => {
            const models = familyToModels.get(family) ?? [];
            const isCollapsed = collapsed.has(family);
            return (
              <Fragment key={family}>
                <tr>
                  <td
                    colSpan={configs.length + 1}
                    className="sticky left-0 border-b border-border/60 bg-muted/50"
                  >
                    <button
                      type="button"
                      onClick={() => toggleFamily(family)}
                      className="flex w-full items-center gap-1.5 px-2 py-1 text-left text-xs font-medium hover:text-foreground"
                    >
                      {isCollapsed ? (
                        <ChevronRight className="size-3.5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="size-3.5 text-muted-foreground" />
                      )}
                      <span>{family}</span>
                      <span className="font-normal text-muted-foreground">
                        {models.length} model{models.length === 1 ? "" : "s"}
                      </span>
                    </button>
                  </td>
                </tr>
                {!isCollapsed &&
                  models.map((model) => (
                    <tr key={model} className="hover:bg-muted/20">
                      <td className={modelCell} title={model}>
                        {model}
                      </td>
                      {configs.map((config) => {
                        const list = (byKey.get(`${model} ${config}`) ?? []).slice();
                        const tierGroups = new Map<string, CryodCatalogCell[]>();
                        for (const cell of list) {
                          const tier = tierOf(cell);
                          const group = tierGroups.get(tier);
                          if (group) group.push(cell);
                          else tierGroups.set(tier, [cell]);
                        }
                        const tiers = [...tierGroups.keys()].sort(
                          (left, right) => tierIndex(left) - tierIndex(right),
                        );
                        return (
                          <td key={config} className={dataCell}>
                            {tiers.length === 0 ? (
                              <span className="text-muted-foreground/30">–</span>
                            ) : (
                              <div className="flex flex-wrap items-center justify-center gap-1">
                                {tiers.map((tier) => (
                                  <TierChip
                                    key={tier}
                                    tier={tier}
                                    cellsAtTier={tierGroups.get(tier) ?? []}
                                    selectedName={selectedName}
                                    onSelect={onSelect}
                                    statusByTestName={statusByTestName}
                                  />
                                ))}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
